import { useEffect, useRef } from "react";
import { Check, CheckCheck, Clock, AlertCircle, Bot, CheckSquare, Square } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { Attachment } from "@/components/Attachment";

export function Ticks({ status }) {
  const cls = "w-3.5 h-3.5 inline-block";
  if (status === "Read") return <CheckCheck className={`${cls} text-[#3B82F6]`} data-testid="tick-read" />;
  if (status === "Delivered") return <CheckCheck className={`${cls} text-white/70`} data-testid="tick-delivered" />;
  if (status === "Sent") return <Check className={`${cls} text-white/70`} data-testid="tick-sent" />;
  if (status === "Failed") return <AlertCircle className={`${cls} text-[#F83A84]`} data-testid="tick-failed" />;
  return <Clock className={`${cls} text-white/70`} data-testid="tick-queued" />;
}

export function Bubble({ m, selectable, selected, onToggle, onRetry }) {
  const out = m.direction === "outbound";
  const hasMedia = ["image", "document", "video", "audio", "sticker"].includes(m.type) || Boolean(m.media?.url);
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"} items-end gap-2`} data-testid={`message-${m.id}`}>
      {selectable && !out && (
        <button onClick={() => onToggle(m.id)} className="mb-2 text-[#101D51]" data-testid={`select-msg-${m.id}`}>
          {selected ? <CheckSquare className="w-5 h-5 text-[#0B7C5A]" /> : <Square className="w-5 h-5 text-[#6B7189]" />}
        </button>
      )}
      <div onClick={selectable && !out ? () => onToggle(m.id) : undefined}
        className={`max-w-[78%] sm:max-w-[65%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          out ? "bg-[#101D51] text-white rounded-br-md" : "bg-white text-[#101D51] rounded-bl-md border border-[#E6E8F0]"}
          ${selectable && !out ? "cursor-pointer" : ""} ${selected ? "ring-2 ring-[#0B7C5A]" : ""}`}>
        {m.is_automated && <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-70 mb-1"><Bot className="w-3 h-3" /> Auto · {m.template_name}</div>}
        {hasMedia && <Attachment m={m} dark={out} />}
        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
        {m.enquiry_id && <div className={`text-[10px] mt-1 ${out ? "text-white/60" : "text-[#0B7C5A]"}`}>Linked to enquiry</div>}
        <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${out ? "text-white/60" : "text-[#6B7189]"}`}>
          {formatDateTime(m.timestamp)} {out && <Ticks status={m.status} />}
          {out && m.status === "Failed" && onRetry && <button onClick={() => onRetry(m.id)} className="underline ml-1" data-testid={`retry-${m.id}`}>Retry</button>}
        </div>
        {out && m.status === "Failed" && m.error && <div className="text-[10px] text-[#F83A84] mt-0.5" data-testid={`error-${m.id}`}>{m.error}</div>}
      </div>
    </div>
  );
}

export function ChatThread({ messages, selectable, selected, onToggle, onRetry, className = "" }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages?.length]);
  return (
    <div className={`space-y-2 p-3 sm:p-4 bg-[#F5F6FA] ${className}`} data-testid="chat-thread">
      {(messages || []).map((m) => (
        <Bubble key={m.id} m={m} selectable={selectable} selected={selected?.has(m.id)} onToggle={onToggle} onRetry={onRetry} />
      ))}
      <div ref={endRef} />
    </div>
  );
}
